<template>
  <div>
    <div v-if="!state.surveyQuestionsSorted.length" class="text-sm text-gray-500">No question-level survey data in scope.</div>
    <div v-else class="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <div class="lg:col-span-3 border rounded-lg bg-white overflow-x-auto">
        <div class="px-3 py-2 border-b flex items-center justify-between">
          <div class="text-sm font-semibold">Questions</div>
          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-600">Sort</label>
            <select v-model="surveyQuestionSortModeProxy" class="border rounded px-2 py-1 text-xs">
              <option value="unanswered_desc">Unanswered (high to low)</option>
              <option value="answer_rate_asc">Answer rate (low to high)</option>
              <option value="answer_rate_desc">Answer rate (high to low)</option>
              <option value="eligible_desc">Eligible (high to low)</option>
            </select>
          </div>
        </div>
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 text-gray-600">
            <tr>
              <th class="text-left px-3 py-2">Question</th>
              <th class="text-left px-3 py-2">Type</th>
              <th class="text-right px-3 py-2">
                Eligible
                <span class="ml-1 text-gray-400 cursor-help text-xs info-tip" :title="tooltips.survey_eligible" role="img" aria-label="Info">ⓘ</span>
              </th>
              <th class="text-right px-3 py-2">Answered</th>
              <th class="text-right px-3 py-2">Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="q in state.surveyQuestionsSorted"
              :key="`q-${q.question_key}`"
              :class="['border-t cursor-pointer', state.selectedSurveyQuestion?.question_key === q.question_key ? 'bg-gray-50' : 'hover:bg-gray-50']"
              @click="actions.selectSurveyQuestion(q.question_key)"
            >
              <td class="px-3 py-2">
                <div class="font-medium">{{ q.question_title || q.question_key }}</div>
                <div class="text-xs text-gray-500">{{ q.question_key }}</div>
              </td>
              <td class="px-3 py-2">{{ q.question_type }}</td>
              <td class="px-3 py-2 text-right">{{ q.eligible_count }}</td>
              <td class="px-3 py-2 text-right">{{ q.answered_count }}</td>
              <td class="px-3 py-2 text-right">{{ q.answer_rate_pct }}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="lg:col-span-2 border rounded-lg bg-white p-4">
        <div v-if="state.selectedSurveyQuestion">
          <div class="text-sm font-semibold mb-1">{{ state.selectedSurveyQuestion.question_title || state.selectedSurveyQuestion.question_key }}</div>
          <div class="text-xs text-gray-600 mb-3">{{ state.selectedSurveyQuestion.question_type }}</div>
          <div class="grid grid-cols-3 gap-2 text-xs mb-4">
            <div class="border rounded p-2">
              <div class="text-gray-500">Eligible <span class="ml-1 text-gray-400 cursor-help text-xs info-tip" :title="tooltips.survey_eligible" role="img" aria-label="Info">ⓘ</span></div>
              <div class="font-semibold">{{ state.selectedSurveyQuestion.eligible_count || 0 }}</div>
            </div>
            <div class="border rounded p-2">
              <div class="text-gray-500">Answered</div>
              <div class="font-semibold">{{ state.selectedSurveyQuestion.answered_count || 0 }}</div>
            </div>
            <div class="border rounded p-2">
              <div class="text-gray-500">Unanswered</div>
              <div class="font-semibold">{{ state.selectedSurveyQuestion.unanswered_count || 0 }}</div>
            </div>
          </div>

          <div v-if="actions.isBinaryQuestion(state.selectedSurveyQuestion)" class="text-xs text-green-700 mb-2">Yes/No question split</div>
          <div v-if="state.selectedSurveyQuestionOptions.length">
            <div class="text-xs text-gray-600 mb-2">Answer split (of answered responses)</div>
            <div v-for="opt in state.selectedSurveyQuestionOptions" :key="`${state.selectedSurveyQuestion.question_key}-${opt.value}`" class="mb-3">
              <div class="flex justify-between text-xs mb-1">
                <span class="truncate max-w-[70%]" :title="opt.label">{{ opt.label || opt.value }}</span>
                <span>{{ opt.count }} ({{ opt.percent_of_answered || 0 }}%)</span>
              </div>
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 rounded-full bg-gradient-to-r from-black to-gray-600" :style="{ width: actions.surveyOptionWidth(opt) }"></div>
              </div>
            </div>
          </div>
          <div v-else class="text-xs text-gray-500">No option-level split available for this question type yet.</div>

          <div class="mt-5 pt-4 border-t">
            <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div class="text-xs font-semibold text-gray-700">Per-ceremony breakdown (selected question)</div>
              <div class="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                <label class="text-xs text-gray-600">View</label>
                <select v-model="surveyQuestionCeremonyViewModeProxy" class="border rounded px-2 py-1 text-xs max-w-full">
                  <option value="table">Table</option>
                  <option value="chart">Chart</option>
                </select>
                <label class="text-xs text-gray-600 ml-2">Sort</label>
                <select v-model="surveyQuestionCeremonySortModeProxy" class="border rounded px-2 py-1 text-xs max-w-full">
                  <option value="date_asc">Date (oldest first)</option>
                  <option value="date_desc">Date (newest first)</option>
                  <option value="answer_rate_desc">Answer rate (high to low)</option>
                  <option value="answered_desc">Answered (high to low)</option>
                  <option value="eligible_desc">Eligible (high to low)</option>
                </select>
                <template v-if="state.surveyQuestionCeremonyViewMode === 'chart'">
                  <label class="text-xs text-gray-600 ml-2">Metric</label>
                  <select v-model="surveyQuestionCeremonyChartMetricProxy" class="border rounded px-2 py-1 text-xs max-w-full">
                    <option value="answer_rate_pct">Answer rate %</option>
                    <option value="answered_count">Answered</option>
                    <option value="eligible_count">Eligible</option>
                  </select>
                </template>
              </div>
            </div>
            <div v-if="state.surveyQuestionCeremonyLoading" class="text-xs text-gray-500">Loading per-ceremony question data...</div>
            <div v-else-if="state.surveyQuestionCeremonyError" class="text-xs text-red-600">{{ state.surveyQuestionCeremonyError }}</div>
            <div v-else-if="!state.surveyQuestionCeremonyRowsSorted.length" class="text-xs text-gray-500">No ceremony-level breakdown for this question in the selected scope.</div>
            <div v-else-if="state.surveyQuestionCeremonyViewMode === 'table'" class="overflow-x-auto border rounded-lg bg-white">
              <table class="min-w-full text-xs">
                <thead class="bg-gray-50 text-gray-600">
                  <tr>
                    <th class="text-left px-3 py-2">Ceremony</th>
                    <th class="text-right px-3 py-2">Eligible</th>
                    <th class="text-right px-3 py-2">Answered</th>
                    <th class="text-right px-3 py-2">Answer rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in state.surveyQuestionCeremonyRowsSorted" :key="`question-ceremony-${state.selectedSurveyQuestion.question_key}-${row.ceremony_id}`" class="border-t">
                    <td class="px-3 py-2">
                      <div class="font-medium">{{ actions.ceremonyTitle(row) }}</div>
                      <div class="text-[11px] text-gray-500">{{ row.ceremony_id }}</div>
                    </td>
                    <td class="px-3 py-2 text-right">{{ row.eligible_count }}</td>
                    <td class="px-3 py-2 text-right">{{ row.answered_count }}</td>
                    <td class="px-3 py-2 text-right">{{ row.answer_rate_pct }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="border rounded-lg bg-white p-3">
              <div class="text-[11px] text-gray-600 mb-2">Visual comparison by ceremony ({{ state.surveyQuestionCeremonyChartMetricLabel }})</div>
              <div class="space-y-2 max-h-[220px] overflow-auto pr-1">
                <div v-for="row in state.surveyQuestionCeremonyRowsSorted" :key="`question-ceremony-chart-${state.selectedSurveyQuestion.question_key}-${row.ceremony_id}`">
                  <div class="flex justify-between text-[11px] mb-1">
                    <span class="truncate max-w-[70%]" :title="actions.ceremonyTitle(row)">{{ actions.ceremonyTitle(row) }}</span>
                    <span>{{ actions.surveyQuestionMetricValue(row, state.surveyQuestionCeremonyChartMetric) }}<template v-if="state.surveyQuestionCeremonyChartMetric === 'answer_rate_pct'">%</template></span>
                  </div>
                  <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div class="h-2 rounded-full bg-gradient-to-r from-black to-gray-600" :style="{ width: actions.surveyQuestionMetricWidth(row, state.surveyQuestionCeremonyChartMetric) }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-sm text-gray-500">Select a question to see answer splits.</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SurveyQuestionsSection',
  props: {
    state: { type: Object, required: true },
    actions: { type: Object, required: true },
    tooltips: { type: Object, default: () => ({}) },
  },
  computed: {
    surveyQuestionSortModeProxy: {
      get() { return this.state.surveyQuestionSortMode },
      set(value) { this.$emit('update:surveyQuestionSortMode', value) },
    },
    surveyQuestionCeremonyViewModeProxy: {
      get() { return this.state.surveyQuestionCeremonyViewMode },
      set(value) { this.$emit('update:surveyQuestionCeremonyViewMode', value) },
    },
    surveyQuestionCeremonySortModeProxy: {
      get() { return this.state.surveyQuestionCeremonySortMode },
      set(value) { this.$emit('update:surveyQuestionCeremonySortMode', value) },
    },
    surveyQuestionCeremonyChartMetricProxy: {
      get() { return this.state.surveyQuestionCeremonyChartMetric },
      set(value) { this.$emit('update:surveyQuestionCeremonyChartMetric', value) },
    },
  },
}
</script>
